import { ExtremeGridEntities } from '@bimser/common';
import { BCDataGrid } from '@bimser/components';
import { IDictionary, IItem, isNullOrUndefined, MLHelper } from '@bimser/core';
import get from 'lodash/get';
import DataGrid2 from '../../..';
import { IGetDataSourceData } from '../../../../../../../entities/common/DataSource/IGetDataSourceData';
import { getDataSourceData } from '../../../../../../../helper';
import { getActionValue } from '../../../../../../../sagas/ruleValidation/action';
import Control from '../../../../../base/Control';
import { IPropertyChangedEventArgs } from '../../../../../entities/EventArgs';
import { getValueControl } from '../../../../../helper';
import { IComboBoxProperties } from '../../../../ComboBox/entities/IComboBoxProperties';
import { IDataGridColumn } from '../../../entities';

export const getDataSource = (
	bcDataGrid: DataGrid2,
	bcColumn: IDataGridColumn<IComboBoxProperties>
): BCDataGrid.Components.ArrayStore | BCDataGrid.Components.DataSource => {
	if (
		bcColumn.editControl.dataSourceType === 'static' ||
		bcColumn.editControl.dataSource.runAtServer
	) {
		return getStaticDataSource(bcDataGrid, bcColumn);
	}

	return getDynamicDataSource(bcDataGrid, bcColumn);
};

const getStaticDataSource = (
	bcDataGrid: DataGrid2,
	bcColumn: IDataGridColumn<IComboBoxProperties>
): BCDataGrid.Components.DataSource | BCDataGrid.Components.ArrayStore => {
	if (!bcDataGrid.__comboBoxes[bcColumn.name]) {
		bcDataGrid.__comboBoxes[bcColumn.name] = {
			store: new BCDataGrid.Components.ArrayStore({
				key: 'value',
				data: generateStaticItems(bcColumn)
			})
		};
	}

	return bcDataGrid.__comboBoxes[bcColumn.name].store;
};

const getDataSourceArgs = (
	bcDataGrid: DataGrid2,
	bcColumn: IDataGridColumn<IComboBoxProperties>
): IGetDataSourceData => {
	return {
		dataSource: bcColumn.editControl.dataSource,
		projectName: bcDataGrid.__form.projectName,
		reload: true,
		forceRefresh: false,
		clientCacheId: bcDataGrid.__form.__windowId,
		projectVersion: bcDataGrid.__form.packageVersion,
		fieldParams: {
			...getFieldParams(bcDataGrid, bcColumn),
			...getSelfFieldParams(bcDataGrid, bcColumn)
		}
	};
};

const getDynamicDataSource = (
	bcDataGrid: DataGrid2,
	bcColumn: IDataGridColumn<IComboBoxProperties>
): BCDataGrid.Components.DataSource | BCDataGrid.Components.ArrayStore => {
	if (!bcDataGrid.__comboBoxes[bcColumn.name]) {
		const valueExpr = bcColumn.editControl.dataSource.valueExpr;

		bcDataGrid.__comboBoxes[bcColumn.name] = {
			store: new BCDataGrid.Components.DataSource({
				key: 'value',
				paginate: false,
				store: new BCDataGrid.Components.CustomStore({
					key: 'value',
					async byKey(key) {
						try {
							bcDataGrid.__comboBoxes[bcColumn.name]?.setLoading?.(true);

							const response = await getDataSourceData({
								...getDataSourceArgs(bcDataGrid, bcColumn),
								loadOptions: {
									valueExpr,
									distinct: true,
									filterNulls: true,
									filters: [
										{
											Column: valueExpr,
											Type: ExtremeGridEntities.FilterType.Equal,
											ItemType: getItemTypeByValueType(bcColumn),
											Not: false,
											Value: key
										}
									],
									sorts: [],
									pagination: { skip: 0, take: 1 }
								}
							});

							const items = generateDynamicItems(bcColumn, response.data.result);

							return items[0];
						} catch (error) {
							throw error;
						} finally {
							bcDataGrid.__comboBoxes[bcColumn.name]?.setLoading?.(false);
						}
					},
					async load(loadOptions) {
						const response = await getDataSourceData({
							...getDataSourceArgs(bcDataGrid, bcColumn),
							loadOptions: {
								valueExpr,
								distinct: true,
								filterNulls: true,
								filters: getFilters(bcColumn, loadOptions.searchValue),
								sorts: [],
								pagination: { skip: 0, take: 100 }
							}
						});

						const dynamicItems = generateDynamicItems(bcColumn, response.data.result);

						const data = await onCellChangeSelectOptions(
							bcDataGrid,
							bcColumn,
							dynamicItems
						);

						return { totalCount: response.data.count, data };
					}
				})
			})
		};
	}

	bcColumn.editControl.dataSource.parameters.forEach(parameter => {
		if (parameter.valueModifier !== 'fieldSelection') {
			return;
		}

		const fieldArr = parameter.field.split('.');
		const controlId = fieldArr.shift();

		const control: Control = bcDataGrid.__form[controlId];

		if (!control || bcDataGrid.__comboBoxes[bcColumn.name].subscribed) {
			return;
		}

		const propertyPath = fieldArr.join('.');

		control.subscribe(
			'event:OnPropertyChanged',
			async (args: IPropertyChangedEventArgs<any>) => {
				await bcDataGrid.__form.applyChanges();

				if (propertyPath === args.name) {
					const dataSource = bcDataGrid.__comboBoxes[bcColumn.name];

					if (dataSource) {
						const store = dataSource.store as BCDataGrid.Components.DataSource;

						if (dataSource.comboBox) {
							const selectBox = dataSource.comboBox.instance();

							if (selectBox) {
								selectBox.option('value', null);
							}
						}

						await store.reload();
					}
				}
			}
		);

		bcDataGrid.__comboBoxes[bcColumn.name].subscribed = true;
	});

	return bcDataGrid.__comboBoxes[bcColumn.name].store;
};

const generateStaticItems = (bcColumn: IDataGridColumn<IComboBoxProperties>) => {
	switch (bcColumn.dbType.type) {
		case 'object': {
			return bcColumn.editControl.items.map(item => ({
				...item,
				text: MLHelper.getMLText(item.text)
			}));
		}
		case 'decimal': {
			return bcColumn.editControl.items.map(item => ({
				...item,
				value: Number(item.value),
				text: item.value?.toString?.()
			}));
		}
		default: {
			return bcColumn.editControl.items.map(item => ({
				...item,
				text: item.value?.toString?.()
			}));
		}
	}
};

const generateDynamicItems = (bcColumn: IDataGridColumn<IComboBoxProperties>, result: any[]) => {
	const valueExpr = bcColumn.editControl.dataSource.valueExpr;

	if (bcColumn.dbType.type === 'object') {
		return result.map(item => ({
			text:
				bcColumn.editControl.dataSource.displayExpr
					.map(displayExpr => item[displayExpr])
					.join(' ') || item[valueExpr],
			value: item[valueExpr],
			externalData: item
		}));
	}

	return result.map(item => ({
		text: item[valueExpr]?.toString?.(),
		value: item[valueExpr],
		externalData: item
	}));
};

const onCellChangeSelectOptions = async (
	dataGrid: DataGrid2,
	bcColumn: IDataGridColumn<IComboBoxProperties>,
	items: IItem[]
) => {
	const editingRow = dataGrid.__bcDataGridRef.current
		?.instance()
		?.getVisibleRows()
		.find(row => row.isEditing);

	const args = {
		columnName: bcColumn.name,
		data: editingRow?.data || {},
		items
	};

	await dataGrid.emit('event:OnCellChangeSelectOptions', args);

	return args.items;
};

const getFilters = (bcColumn: IDataGridColumn<IComboBoxProperties>, searchValue?: string) => {
	if (!isNullOrUndefined(searchValue) && searchValue !== '') {
		let filters!: ExtremeGridEntities.ILoadFilterItem[];

		if (bcColumn.dbType.type === 'object') {
			filters = bcColumn.editControl.dataSource.displayExpr.map(expr => ({
				Column: expr,
				Not: false,
				Type: ExtremeGridEntities.FilterType.Contain,
				Value: searchValue,
				ItemType: ExtremeGridEntities.ItemType.String
			}));
		} else {
			const itemType = getItemTypeByValueType(bcColumn);

			filters = [
				{
					Column: bcColumn.editControl.dataSource.valueExpr,
					Not: false,
					Type:
						itemType === ExtremeGridEntities.ItemType.String
							? ExtremeGridEntities.FilterType.Contain
							: ExtremeGridEntities.FilterType.Equal,
					Value: searchValue,
					ItemType: itemType
				}
			];
		}

		return [
			{
				Filters: filters,
				Operator: ExtremeGridEntities.GroupOperator.Or
			}
		];
	}

	return [];
};

const getItemTypeByValueType = (bcColumn: IDataGridColumn<IComboBoxProperties>) => {
	switch (bcColumn.editControl.valueType) {
		case 'integer': {
			return ExtremeGridEntities.ItemType.Int32;
		}
		case 'decimal': {
			return ExtremeGridEntities.ItemType.Decimal;
		}
		case 'string':
		default: {
			return ExtremeGridEntities.ItemType.String;
		}
	}
};

const getFieldParams = (bcDataGrid: DataGrid2, bcColumn: IDataGridColumn<IComboBoxProperties>) =>
	bcColumn.editControl.dataSource.parameters.reduce((prev, parameter) => {
		if (parameter.valueModifier === 'valueEntry') {
			prev[parameter.name] = parameter.value;

			return prev;
		}

		const fieldArr = parameter.field.split('.');
		const controlId = fieldArr.shift();
		const propertyPath = fieldArr.join('.');

		const value = getValueControl(
			bcDataGrid.__form,
			controlId,
			getActionValue(controlId, propertyPath, bcDataGrid.__form.items)
		);

		prev[parameter.field] = value;

		return prev;
	}, {});

const getSelfFieldParams = (
	bcDataGrid: DataGrid2,
	bcColumn: IDataGridColumn<IComboBoxProperties>
) => {
	const control = bcColumn.editControl;
	const fields: string[] = [];

	control.dataSource.parameters.forEach(parameter => {
		if (parameter.valueModifier == 'fieldSelection') {
			fields.push(parameter.field);
		}
	});

	const editingRow = bcDataGrid.__bcDataGridRef.current
		.instance()
		.getVisibleRows()
		.find(row => row.isEditing);

	const fieldParams: IDictionary<any> = {};

	if (editingRow) {
		const row = editingRow.data;

		Object.keys(row).forEach(key => {
			const column = bcDataGrid.columns.find(c => c.name == key);

			switch (column?.editType) {
				case 'comboBox': {
					if (column.dbType.type === 'object') {
						fields
							.filter(path => path.includes(`columns.${key}.data`))
							.forEach(path => {
								fieldParams[path] = get(row, [
									key,
									'externalData',
									path.split('.').at(-1)
								]);
							});
					} else {
						fields
							.filter(path => path.includes(`columns.${key}.value`))
							.forEach(
								cellPropertyPath => (fieldParams[cellPropertyPath] = row[key])
							);
					}

					break;
				}
				case 'textBox':
				case 'timePicker':
				case 'datePicker':
				case 'dateTimePicker':
				case 'numberBox':
				case 'checkBox':
				case 'dateRangePicker': {
					fields
						.filter(path => path.includes(`columns.${key}.value`))
						.forEach(path => {
							fieldParams[path] = row[key];
						});
					break;
				}
				case 'multiLanguageTextBox': {
					fields
						.filter(path => path.includes(`columns.${key}.culture`))
						.forEach(path => {
							const culture = path.split('.').at(-1);
							fieldParams[path] = get(row, `${key}.${culture}`);
						});
					break;
				}
			}
		});
	}

	return fieldParams;
};
